import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { Panel } from '../../platform/primitives/Panel'
import { CategoryChip } from '../../platform/primitives/CategoryChip'
import { CategoryIcon } from '../../platform/icons/CategoryIcon'
import { ListRow, SectionHeader } from '../../platform/primitives/ListRow'
import { CATEGORY_TYPE_LABEL } from './categoryTree'
import { CategoryForm } from './CategoryForm'
import { useCategories } from './useCategories'
import './categories.css'


/**
 * `/categories/:id`, read from the LIST cache rather than `GET /categories/{id}`.
 *
 * The list is already loaded, holds every field this screen shows, and a second
 * request would let the two disagree after an edit — the detail showing a name
 * the list has already replaced. `keys.categories.detail(id)` stays unused
 * until something needs a field the list omits.
 *
 * **`useParams()`, not `useParams<{ id: string }>()`.** `VerifyDevice.tsx`
 * already settled this for the app: the router itself types a param as
 * possibly-undefined (a route can render without matching, in a test harness
 * or via `<Routes>` composed by hand), and a generic that asserts it away just
 * moves the lie from the router's types to this file's. `id` reads as
 * `string | undefined` below and an undefined id simply matches no category,
 * which is the same "not found" branch a stale or mistyped id takes.
 */
export function CategoryDetail() {
  const { id } = useParams()
  const state = useCategories()
  const navigate = useNavigate()
  // One inline form, same shape as `Categories.tsx`'s own — a second boolean
  // could only ever agree or disagree with this one.
  const [editing, setEditing] = useState(false)
  // Empty until the list has loaded — same reasoning as `Categories.tsx`.

  return (
    <Panel>
      <Async
        surface="categories"
        state={state}
        skeleton={
          <>
            <Skeleton height={28} width={180} />
            <Skeleton height={24} width={96} />
            <Skeleton height={16} />
            <Skeleton height={16} />
          </>
        }
        empty={{ title: 'No categories' }}
      >
        {(categories) => {
          const category = categories.find((c) => c.id === id)

          /* Not the 404 route: the chrome, the nav and the rest of the list are
             all still valid, and replacing the whole page would strand someone
             who followed a link to a category they have since renamed away. */
          if (!category) return <p className="route-note">Category not found.</p>

          if (editing) {
            return (
              // `key` — fix round 2, matching `Categories.tsx:104`'s own fix
              // for the identical class of bug. `editing` here is a plain
              // boolean and `category` is re-resolved from `:id` fresh on
              // every render, so without a key tied to the category itself, a
              // `:id` change while `editing` stays `true` re-renders this
              // SAME `CategoryForm` instance rather than remounting it — and
              // its `name`/`type`/`color` are seeded only once, in
              // `useState`'s initial value. The list version needed two Edit
              // clicks to reach that; this screen shows only one category at
              // a time and renders nothing navigable while `editing` is
              // `true` (this branch returns before the children list below),
              // so the only route in is browser back/forward across two
              // already-visited `/categories/:id` pages — the SAME mounted
              // route element, param changed, `editing` surviving across it.
              <CategoryForm
                key={category.id}
                onClose={() => setEditing(false)}
                editing={category}
              />
            )
          }

          // `find`/`filter` against `parent_id`, never an explicit `=== null`
          // or `!== null` on it — the wire type is `string | null | undefined`
          // and every category `id` is a real string, so neither comparison can
          // spuriously match and both `undefined` and `null` fall out of the
          // lookups on their own, the same way `Row`'s `sublabel` in
          // `Categories.tsx` uses `??` rather than a null check.
          const parent = categories.find((c) => c.id === category.parent_id)
          const children = categories.filter((c) => c.parent_id === category.id)

          return (
            <>
              <div className="categories__detail-header">
                <h2 className="categories__title">{category.name}</h2>
                {/* Same condition, same accessible-name shape as Task 9's list
                    row: a category editable from the list and not from its own
                    detail is the inconsistency a reviewer finds first. */}
                {!category.is_system && (
                  <button
                    type="button"
                    className="categories__edit hoverable"
                    onClick={() => setEditing(true)}
                  >
                    {`Edit ${category.name}`}
                  </button>
                )}
              </div>
              {/* `size="chip"` — CategoryChip's other size, first exercised here. */}
              <CategoryChip name={category.name} size="chip" />
              <dl className="categories__facts">
                <dt>Type</dt>
                <dd>{CATEGORY_TYPE_LABEL[category.category_type]}</dd>
                {parent && (
                  <>
                    <dt>Within</dt>
                    <dd>{parent.name}</dd>
                  </>
                )}
              </dl>
              {category.description && <p>{category.description}</p>}
              {children.length > 0 && (
                <section>
                  <SectionHeader title="Subcategories" count={children.length} />
                  {children.map((child) => (
                    <ListRow
                      key={child.id}
                      label={child.name}
                      leading={
                        <span className="categories__glyph">
                          <CategoryIcon category={child} />
                        </span>
                      }
                      onActivate={() => navigate(`/categories/${child.id}`)}
                    />
                  ))}
                </section>
              )}
            </>
          )
        }}
      </Async>
    </Panel>
  )
}
